import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Redirect, Switch, useLocation, useHistory } from "react-router-dom";
import Loader from "react-loader-spinner";
import { Pane, toaster } from "evergreen-ui";
import { HeaderApp, PrivateRoute, SidebarApp } from "./components";
import {
  AccountingScreen,
  InvoiceFormScreen,
  ZohoScreen,
  ReceiptReport,
  ReportRequest,
} from "./screens";
import ControlHubContext from "./contexts/controlhub";
import { API_URL, FREE_PLAN_NAME, APP_URL } from "./config";
import ControlhubService from "./services/controlhub-service";

function App() {
  const history = useHistory();
  const { search } = useLocation();
  const [configuration, setConfiguration] = useState({});
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const [cookieSession, setCookieSession] = useState(null);
  const [currentCompanyInfo, setCurrentCompanyInfo] = useState({});
  const [currentCompanyPlan, setCurrentCompanyPlan] = useState(null);
  const [currentUserId, setCurrentUserId] = useState("");
  const [currentTab, setCurrentTab] = useState({});
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [context, setContext] = useState({});
  const [companies, setCompanies] = useState([]);
  const [controlhubService] = useState(new ControlhubService());
  const [loading, setLoading] = useState(false);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const URLToken = useMemo(() => searchParams.get("token"), [searchParams]);

  useEffect(() => {
    setContext({
      configuration,
      currentCompanyId,
      currentCompanyInfo,
      currentCompanyPlan,
      currentUserId,
      isSuperuser,
      cookieSession,
    });
  }, [
    configuration,
    currentCompanyId,
    currentCompanyInfo,
    currentCompanyPlan,
    currentUserId,
    isSuperuser,
    cookieSession,
  ]);

  const handleDeleteTokenParam = useCallback(() => {
    const newSearchParams = new URLSearchParams(search);
    newSearchParams.delete("token");
    history.push({ search: newSearchParams.toString() });
  }, [search, history]);

  const messageUndefinedURLError = useCallback(() => {
    toaster.danger("REACT_APP_WEB_URL is not defined");
  }, []);

  useEffect(() => {
    const getTokenSession = async () => {
      try {
        const request = await fetch(`${API_URL}/admin/session`, {
          credentials: "include",
          headers: { "Admin-Token": `${URLToken}` },
        });
        const { success } = await request.json();
        if (!success) throw new Error();
        handleDeleteTokenParam();
        setCookieSession(true);
      } catch (error) {
        if (!APP_URL) {
          messageUndefinedURLError();
          return;
        }
        window.location.href = `${APP_URL}/`;
      }
    };
    getTokenSession();
  }, [URLToken, messageUndefinedURLError, handleDeleteTokenParam]);

  useEffect(() => {
    if (cookieSession) {
      // TODO: Move all fetches calls to the service and start using await/async ES6 functions
      fetch(`${API_URL}/admin/users/me`, {
        credentials: "include",
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("HTTP status " + res.status);
          }
          return res.json();
        })
        .then((data) => {
          setCurrentCompanyId(data.company_id);
          setCurrentCompanyPlan(data.plan);
          setCurrentUserId(data.user_id);
          setIsSuperuser(data.superuser);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [cookieSession]);

  useEffect(() => {
    if (isSuperuser) {
      fetch(`${API_URL}/admin/companies`, {
        credentials: "include",
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("HTTP status " + res.status);
          }
          return res.json();
        })
        .then((data) => {
          setCompanies(data);
          setCurrentCompanyInfo(
            data.filter((c) => c.id === currentCompanyId)[0]
          );
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [currentCompanyId, isSuperuser]);

  useEffect(() => {
    if (currentCompanyId && cookieSession) {
      fetch(`${API_URL}/admin/companies/${currentCompanyId}/configuration`, {
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (!data.success) {
            throw new Error("Couldn't fetch company configuration");
          }
          setConfiguration(data.data);
        })
        .catch(() => {
          setConfiguration();
        });
    }
  }, [currentCompanyId, cookieSession]);

  const onChangeCompany = (companyId) => {
    setCurrentCompanyId(companyId);
  };

  const handleDefaultRedirect = () => {
    if (!currentCompanyPlan) {
      return null;
    }

    let redirect = null;

    switch (currentCompanyPlan) {
      case FREE_PLAN_NAME:
        redirect = <Redirect to="/settings/billing" />;
        break;
      default:
        redirect = <Redirect to="/reports/dashboard" />;
    }

    return redirect;
  };

  const handleCurrentTab = useCallback((tab) => {
    setCurrentTab(tab || {});
  }, []);

  const handleChangeLoadingState = useCallback(
    (showLoading) => setLoading(showLoading),
    []
  );

  const handleRedirectToAppDashboard = useCallback(() => {
    if (!APP_URL) {
      messageUndefinedURLError();
      return;
    }

    window.location.href = `${APP_URL}/reports/dashboard`;
    return null;
  }, [messageUndefinedURLError]);

  if (!cookieSession) {
    return <div>Loading...</div>;
  }

  return (
    <ControlHubContext.Provider value={context}>
      {loading && (
        <Pane
          width="100%"
          height="100%"
          position="fixed"
          display="flex"
          backgroundColor="#ffffff96"
          textAlign="center"
          alignItems="center"
          zIndex={1}
        >
          <Pane width={100} height={100} margin="auto">
            <Loader type="ThreeDots" color="#00BFFF" height={100} width={100} />
          </Pane>
        </Pane>
      )}
      <HeaderApp companies={companies} onChangeCompany={onChangeCompany} />
      <SidebarApp onChangeCurrentTab={handleCurrentTab}>
        <Switch>
          <PrivateRoute path="/reports/dashboard">
            {handleRedirectToAppDashboard}
          </PrivateRoute>

          <PrivateRoute path="/reports/card-purchases">
            <ZohoScreen frameName="card-purchases" />
          </PrivateRoute>

          <PrivateRoute path="/reports/amazon-purchases">
            <ZohoScreen frameName="amazon-purchases" />
          </PrivateRoute>

          <PrivateRoute path="/purchases-reports">
            <Redirect to="/reports/card-purchases" />
          </PrivateRoute>

          <PrivateRoute path="/reports/request-reports">
            <ReportRequest />
          </PrivateRoute>

          <PrivateRoute path="/reports/itemized-request-reports">
            <ZohoScreen frameName="itemized" />
          </PrivateRoute>

          <PrivateRoute path="/reports/receiving-reports">
            <ZohoScreen frameName="receiving" />
          </PrivateRoute>

          <PrivateRoute path="/reports/logs">
            <ZohoScreen frameName="logs" />
          </PrivateRoute>

          <PrivateRoute path="/reports">
            <Redirect to="/reports/dashboard" />
          </PrivateRoute>

          <PrivateRoute path="/accruals">
            <ZohoScreen frameName="accruals" />
          </PrivateRoute>

          <PrivateRoute path="/receipt/:id">
            <ReceiptReport />
          </PrivateRoute>

          <PrivateRoute path="/invoice/:invoice_id">
            <InvoiceFormScreen />
          </PrivateRoute>

          <PrivateRoute path="/settings">
            <AccountingScreen
              currentTab={currentTab}
              service={controlhubService}
              setLoadingState={handleChangeLoadingState}
            />
          </PrivateRoute>

          <PrivateRoute exact path="/">
            {handleDefaultRedirect}
          </PrivateRoute>
        </Switch>
      </SidebarApp>
    </ControlHubContext.Provider>
  );
}

export default App;
